'use client';

import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { usePrivy } from '@privy-io/react-auth';
import { useAtomValue, useAtom } from 'jotai';
import { SparklesIcon, EllipsisHorizontalIcon } from '@heroicons/react/16/solid';
import { DelegateInfo } from '@/app/types/delegates';
import { tokenInfoAtom } from '@/app/atoms/token';
import { userInfoAtom } from '@/app/atoms/users';
import CardDelegationModal from '@/app/components/Modals/CardDelegationModal';
import { UniversalAvatar } from '@/app/components/UniversalAvatar';
import { UsersAccountReturnType } from '@/app/types/account';
import { formatVotingPower } from '@/app/helpers/formatVotingPower';

type MergedDelegateInfo = DelegateInfo & UsersAccountReturnType;

interface DelegateCardProps {
  delegate: MergedDelegateInfo;
}

const DelegateCard = ({ delegate }: DelegateCardProps) => {
  const pathname = usePathname();
  const [action, setAction] = useState<'delegate' | 'revoke'>('delegate');
  const [isDelegateModalOpen, setIsDelegateModalOpen] = useState(false);
  const [settings, setSettings] = useState<UsersAccountReturnType | undefined>();
  const { user } = usePrivy();
  const address = user?.wallet?.address as `0x${string}` | undefined;
  const tokenInfo = useAtomValue(tokenInfoAtom);
  const { symbol } = tokenInfo;
  const [users] = useAtom(userInfoAtom);
  const [activeModalDelegate, setActiveModalDelegate] = useState<`0x${string}` | null>(
    null,
  );
  const userIsSelfDelegated = delegate?.delegators?.includes(address as `0x${string}`);
  const selfDelegatedOnly = delegate.delegationsReceived === 1 && userIsSelfDelegated;
  const isUsersDelegate = delegate.delegators?.includes(address as `0x${string}`);
  const votingPower = formatVotingPower(delegate?.votingPower!);

  useEffect(() => {
    const newSettings = users.find(
      (user: UsersAccountReturnType) => user.wallet_address === delegate.address,
    );
    setSettings(newSettings);
  }, [delegate.delegators, delegate.address, users, pathname]);

  const handleClick = (action: 'delegate' | 'revoke') => {
    setIsDelegateModalOpen(true);
    setAction(action);
  };

  const handleCloseModal = () => {
    setActiveModalDelegate(null);
    setIsDelegateModalOpen(false);
  };

  return (
    <div
      className={`relative flex flex-col pb-4 border border-neutral-700 xs:rounded-lg min-w-[180px] min-h-[300px] ${isUsersDelegate}`}
    >
      <div className={`flex justify-center min-h-[45px] rounded-t-md w-full pt-2`}>
        {isUsersDelegate ? (
          <>
            <SparklesIcon className="h-4 w-4 text-light" />
            <div className="text-light font-semibold uppercase text-xs px-1">
              Your Delegate
            </div>
            <SparklesIcon className="h-4 w-4 text-light" />
          </>
        ) : settings?.is_seeking_delegation ? (
          <div className="text-yellow font-semibold uppercase text-xs px-1 whitespace-nowrap">
            Seeking Delegations
          </div>
        ) : null}
      </div>
      <div className="flex justify-between w-full px-4 pb-2">
        <div className="flex items-start">
          <UniversalAvatar
            address={delegate?.address!}
            showTokenHolder={!!delegate?.tokenBalance}
          />
        </div>
      </div>
      <div className="px-4">
        <div className="w-full py-2 border-t border-b border-neutral-700 flex justify-center items-center">
          <div className="flex flex-col">
            <div className="flex flex-col md:flex-row justify-center items-center whitespace-wrap">
              <div className="whitespace-nowrap">
                {votingPower} Delegated {String(symbol)}s
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-start mt-2 truncate-3-lines">
          {settings?.bio ?? settings?.delegation_statement}
        </div>
        <div className="absolute bottom-0 left-0 right-0 px-4 mt-4 pb-4 pt-2 rounded-b-md flex justify-between items-end w-full">
          <div className="flex narrow:mt-0 uppercase text-xxs">
            {selfDelegatedOnly ? (
              <div className="flex flex-col">
                <div>Self Delegated</div>
              </div>
            ) : (
              Number(delegate.delegationsReceived) > 0 && (
                <div className="flex flex-col">
                  <div>{`${delegate.delegationsReceived} ${Number(delegate.delegationsReceived) > 1 ? 'Delegators' : 'Delegator'}`}</div>
                </div>
              )
            )}
          </div>
          <div className="group">
            <EllipsisHorizontalIcon className="w-4 h-4 cursor-pointer group-hover:hidden" />
            <div className="absolute bottom-3 right-4 mb-1 group-hover:block hidden">
              {isUsersDelegate ? (
                <button
                  onClick={() => handleClick('revoke')}
                  className="text-sm text-link rounded"
                >
                  Revoke
                </button>
              ) : (
                <button
                  onClick={() => handleClick('delegate')}
                  className="text-sm text-link rounded"
                >
                  Delegate
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {isDelegateModalOpen && (
        <CardDelegationModal
          isOpen={isDelegateModalOpen}
          onClose={handleCloseModal}
          delegateAddress={delegate.address}
          action={action}
          activeModalDelegate={activeModalDelegate}
          setActiveModalDelegate={setActiveModalDelegate}
        />
      )}
    </div>
  );
};

export default DelegateCard;
